import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Nfc, Loader2, CheckCircle2, AlertCircle, Smartphone, Save, GraduationCap, ExternalLink, ShieldCheck } from "lucide-react";
import { isNativeApp, prepareTapToPay, openExternalUrl, getAppleLinkStatus, getTTPCapability } from "../../utils/tapToPay";
import { refreshTTPStatus } from "../../hooks/useTapToPay";
import TapToPayEducation from "../payments/TapToPayEducation";
import TapToPayTryIt from "../payments/TapToPayTryIt";
import { TapToPayGlyph, ttpLabel } from "../payments/TapToPayButton";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;

const REASONS = {
  global_disabled: "Tap to Pay is temporarily unavailable — please try again later.",
  no_stripe_connect: "Connect your Stripe account to enable Tap to Pay.",
  stripe_not_ready: "Finish your Stripe onboarding (charges must be enabled) to use Tap to Pay.",
  business_disabled: "Tap to Pay is available — turn it on below.",
};

export default function TapToPayCard() {
  const [status, setStatus] = useState(null);
  const [txns, setTxns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", postal_code: "" });
  const [eduOpen, setEduOpen] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupMsg, setSetupMsg] = useState("");
  const [setupProgress, setSetupProgress] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkStarted, setLinkStarted] = useState(false);
  const [deviceLinked, setDeviceLinked] = useState(null); // null = unknown
  const [tryItOpen, setTryItOpen] = useState(false);
  const setupDoneRef = useRef(false);
  const navigate = useNavigate();
  const native = isNativeApp();
  const iosNative = native && getTTPCapability().platform === "ios";
  const androidNative = native && getTTPCapability().platform === "android";
  const onb = status?.merchant_onboarding || {};
  // Apple req 1.6: on-device, the authoritative T&C status comes from Apple
  // via the Stripe SDK; the backend record is only a cross-device UX hint.
  // Apple merchant linking is an iOS-only concept — Android device setup is
  // never gated on it (Stripe handles Android Tap to Pay terms in the SDK).
  const linkedEffective = androidNative || deviceLinked === true || (deviceLinked !== false && onb.linked);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/terminal/status`, auth());
      setStatus(res.data);
      if (res.data.address) {
        setAddr({
          line1: res.data.address.line1 || "", city: res.data.address.city || "",
          state: res.data.address.state || "", postal_code: res.data.address.postal_code || "",
        });
      }
      if (res.data.business_enabled) {
        const t = await axios.get(`${API}/api/terminal/transactions?limit=10`, auth());
        setTxns(t.data.transactions || []);
      }
    } catch { /* section hides */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apple req 1.6: retrieve the merchant's Apple T&C status from the SDK and
  // auto-sync the backend hint when Apple reports the account as linked.
  useEffect(() => {
    if (!iosNative || !status?.eligible || !status?.connected_account_id) return;
    getAppleLinkStatus({
      token_endpoint: `${API}/api/terminal/connection-token`,
      auth_token: localStorage.getItem("token"),
      connected_account_id: status.connected_account_id,
    }).then(async (res) => {
      if (!res.ok) return; // old build / init failure → keep backend hint
      setDeviceLinked(!!res.linked);
      if (res.linked && !status.merchant_onboarding?.linked) {
        try {
          await axios.post(`${API}/api/terminal/onboarding/confirm`, { source: "sdk_verified" }, auth());
          load();
        } catch { /* non-blocking */ }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iosNative, status?.eligible, status?.connected_account_id]);

  if (!status) return null;

  const toggle = async (v) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/terminal/settings`, { enabled: v }, auth());
      toast.success(v ? "Tap to Pay enabled" : "Tap to Pay disabled");
      refreshTTPStatus("owner");
      load();
      // Apple req 4.1: merchant education right after enabling Tap to Pay.
      if (v) setEduOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update Tap to Pay");
    } finally { setSaving(false); }
  };

  // Apple reqs 3.5/3.9/3.9.1: explicit setup action — connects the reader on
  // this device, which presents Apple's Tap to Pay Terms & Conditions (owner/
  // admin only) and shows configuration progress while the reader prepares.
  const runSetup = async () => {
    setSetupBusy(true);
    setSetupMsg("Starting…");
    setSetupProgress(null);
    const res = await prepareTapToPay({
      token_endpoint: `${API}/api/terminal/connection-token`,
      auth_token: localStorage.getItem("token"),
      location_id: status.location_id,
      connected_account_id: status.connected_account_id,
      merchant_display_name: status.merchant_display_name,
      tos_allowed: true,
    }, { onStatus: (m, _ph, p) => { setSetupMsg(m || ""); setSetupProgress(p ?? null); } });
    setSetupBusy(false);
    setSetupMsg("");
    setSetupProgress(null);
    if (res.ok) {
      toast.success("Tap to Pay is ready on this device");
      setupDoneRef.current = true;
      setEduOpen(true);
    } else if (res.error && res.error !== "busy" && res.error !== "timeout") {
      toast.error(res.error);
    }
  };

  const saveAddress = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/terminal/settings`, { address: addr }, auth());
      toast.success("Business address saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the address");
    } finally { setSaving(false); }
  };

  // Stage 1 — web-based Apple merchant onboarding (Stripe Terminal Onboarding
  // Links). Opens Apple's page in the browser for the owner to sign in with a
  // business Apple ID and accept the Terms for this connected account.
  const startMerchantOnboarding = async () => {
    setLinkBusy(true);
    try {
      const res = await axios.post(`${API}/api/terminal/onboarding/link`, {}, auth());
      if (res.data.redirect_url) {
        openExternalUrl(res.data.redirect_url);
        setLinkStarted(true);
        toast.info("Complete Apple's Terms in the browser, then return here");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Apple merchant onboarding");
    } finally { setLinkBusy(false); }
  };

  const confirmMerchantOnboarding = async () => {
    setLinkBusy(true);
    try {
      await axios.post(`${API}/api/terminal/onboarding/confirm`, {}, auth());
      toast.success("Merchant account linked — technicians can now set up their phones");
      setLinkStarted(false);
      refreshTTPStatus("owner");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm onboarding");
    } finally { setLinkBusy(false); }
  };

  const canToggle = status.global_enabled && status.connect_ready;

  return (
    <Card className="glass-card border-theme" data-testid="tap-to-pay-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Nfc className="w-5 h-5 text-sky-500" /> Tap to Pay
            </CardTitle>
            <CardDescription>
              Accept contactless cards, Apple Pay and Google Pay directly on your phone in the Fieldz AI mobile app — no card reader needed.
            </CardDescription>
          </div>
          {status.eligible
            ? <Badge className="bg-emerald-500/15 text-emerald-500 border-0" data-testid="ttp-status-badge"><CheckCircle2 className="w-3 h-3 mr-1" /> Active</Badge>
            : <Badge className="bg-muted text-muted-foreground border-0" data-testid="ttp-status-badge">Not active</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.eligible && status.reason && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-foreground" data-testid="ttp-reason">
            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            {REASONS[status.reason] || "Tap to Pay is not available yet."}
          </div>
        )}

        {canToggle && (
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium text-foreground">Enable Tap to Pay for this business</p>
              <p className="text-[11px] text-muted-foreground">
                Platform fee: {status.fee.percent}%{status.fee.flat_cents > 0 ? ` + ${money(status.fee.flat_cents / 100)}` : ""} per payment (separate from online payment fees) · Stripe's processing fees apply per your Stripe account
              </p>
            </div>
            <Switch checked={status.business_enabled} disabled={saving} onCheckedChange={toggle} data-testid="ttp-enable-switch" />
          </div>
        )}

        {canToggle && status.needs_address && (
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="ttp-address-form">
            <p className="text-sm font-medium text-foreground">Business address (required by Stripe for Tap to Pay)</p>
            <Input placeholder="Street address" value={addr.line1} onChange={(e) => setAddr(a => ({ ...a, line1: e.target.value }))} data-testid="ttp-addr-line1" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={addr.city} onChange={(e) => setAddr(a => ({ ...a, city: e.target.value }))} data-testid="ttp-addr-city" />
              <Input placeholder="State" maxLength={2} value={addr.state} onChange={(e) => setAddr(a => ({ ...a, state: e.target.value }))} data-testid="ttp-addr-state" />
              <Input placeholder="ZIP" value={addr.postal_code} onChange={(e) => setAddr(a => ({ ...a, postal_code: e.target.value }))} data-testid="ttp-addr-zip" />
            </div>
            <Button size="sm" onClick={saveAddress} disabled={saving} data-testid="ttp-addr-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save address
            </Button>
          </div>
        )}

        {/* Stage 1 — Apple merchant account linking (web onboarding), per connected account */}
        {canToggle && status.business_enabled && (
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="ttp-merchant-onboarding">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-sky-500" /> Apple merchant linking
              </p>
              {linkedEffective
                ? <Badge className="bg-emerald-500/15 text-emerald-500 border-0" data-testid="ttp-onboarding-badge"><CheckCircle2 className="w-3 h-3 mr-1" /> Linked</Badge>
                : <Badge className="bg-amber-500/15 text-amber-500 border-0" data-testid="ttp-onboarding-badge">Action needed</Badge>}
            </div>
            {deviceLinked === false && onb.linked && (
              <p className="text-[11px] text-amber-500" data-testid="ttp-link-mismatch">
                Apple reports the Terms haven't been accepted for this Stripe account yet — use the button below to (re)link the merchant account.
              </p>
            )}
            {linkedEffective ? (
              <p className="text-[11px] text-muted-foreground">
                This Stripe account has accepted Apple's Tap to Pay Terms{onb.merchant_display_name ? ` as "${onb.merchant_display_name}"` : ""}. Your technicians can now set up Tap to Pay on their own iPhones.
              </p>
            ) : (
              <>
                <p className="text-[11px] text-muted-foreground">
                  A business representative must accept Apple's Tap to Pay Terms & Conditions for this Stripe account before anyone can take payments. This opens Apple's secure page — sign in with your business Apple ID and accept.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={startMerchantOnboarding} disabled={linkBusy}
                    className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="ttp-onboarding-link-btn">
                    {linkBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ExternalLink className="w-4 h-4 mr-1" />}
                    {linkStarted ? "Reopen Apple onboarding" : "Link merchant account & accept Apple Terms"}
                  </Button>
                  {linkStarted && (
                    <Button size="sm" variant="outline" onClick={confirmMerchantOnboarding} disabled={linkBusy} data-testid="ttp-onboarding-confirm-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> I've completed Apple linking
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {native && status.eligible && linkedEffective && (
          <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 space-y-2" data-testid="ttp-setup-section">
            <p className="text-sm font-medium text-foreground">Set up {ttpLabel()} on THIS device (optional)</p>
            <p className="text-[11px] text-muted-foreground">
              If you collect payments on this phone yourself, configure its reader now. Technicians set up their own phones from the Technician Portal.
            </p>
            {setupBusy ? (
              <div className="space-y-1.5">
                <p className="text-xs text-foreground flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> {setupMsg || "Preparing…"}
                </p>
                {setupProgress != null && (
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-sky-500 transition-all" style={{ width: `${Math.min(setupProgress, 100)}%` }} />
                  </div>
                )}
              </div>
            ) : (
              <Button size="sm" onClick={runSetup} disabled={status.needs_address}
                className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="ttp-setup-btn">
                <TapToPayGlyph className="w-4 h-4 mr-1.5" /> Set up {ttpLabel()}
              </Button>
            )}
            {status.needs_address && <p className="text-[10px] text-amber-500">Save your business address below first.</p>}
          </div>
        )}

        <div className="rounded-lg border border-border p-3 space-y-1.5">
          <p className="text-sm font-medium text-foreground flex items-center gap-1.5"><Smartphone className="w-4 h-4 text-muted-foreground" /> Compatible devices</p>
          <p className="text-[11px] text-muted-foreground">iPhone: {status.device_requirements?.ios}</p>
          <p className="text-[11px] text-muted-foreground">Android: {status.device_requirements?.android}</p>
          <p className="text-[11px] text-muted-foreground">
            Technicians with the "Collect with Tap to Pay" permission set up their own iPhone from the Technician Portal → My Profile (no Stripe or admin access needed).
          </p>
          <button
            type="button"
            onClick={() => setEduOpen(true)}
            className="flex items-center gap-1.5 text-xs font-medium text-sky-500 hover:text-sky-400 transition-colors pt-1"
            data-testid="ttp-education-link"
          >
            <GraduationCap className="w-3.5 h-3.5" /> How to accept payments with {ttpLabel()}
          </button>
        </div>

        {status.business_enabled && txns.length > 0 && (
          <div className="rounded-lg border border-border p-3" data-testid="ttp-transactions">
            <p className="text-xs font-medium text-muted-foreground mb-2">Recent Tap to Pay payments</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {txns.map(t => (
                <div key={t.stripe_payment_intent_id} className="flex items-center justify-between text-xs py-1 border-b border-border/50 last:border-0">
                  <div className="min-w-0">
                    <span className="text-foreground font-medium">{t.invoice_number}</span>
                    <span className="text-muted-foreground ml-2">{t.customer_name}</span>
                    {t.collected_by_name && <span className="text-muted-foreground ml-2">· {t.collected_by_name}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`border-0 text-[9px] ${t.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-500" : t.payment_status === "canceled" ? "bg-muted text-muted-foreground" : "bg-amber-500/15 text-amber-500"}`}>
                      {t.payment_status}
                    </Badge>
                    <span className="text-foreground tabular-nums font-medium">{money(t.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      <TapToPayEducation open={eduOpen} onClose={() => {
        setEduOpen(false);
        // Apple req 3.9: after setup + education, invite the user to try it.
        if (setupDoneRef.current) { setupDoneRef.current = false; setTryItOpen(true); }
      }} />
      <TapToPayTryIt open={tryItOpen} onClose={() => setTryItOpen(false)}
        onTry={() => navigate("/dashboard/invoices")} />
    </Card>
  );
}
